import { Table, Button, Space, Modal, Form, Input, DatePicker, message, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../utils/api';
import type { Link, PaginatedResponse, CreateLinkRequest } from '../types';

export default function Links() {
  const [links, setLinks] = useState<Link[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<Link | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const res = await api
        .get(`links?page=${page}&pageSize=10`)
        .json<{ success: boolean; data: PaginatedResponse<Link> }>();
      if (res.success && res.data) {
        setLinks(res.data.items);
        setTotal(res.data.total);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [page]);

  const handleCreate = () => {
    setEditingLink(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (link: Link) => {
    setEditingLink(link);
    form.setFieldsValue({
      target_url: link.target_url,
      expires_at: link.expires_at ? dayjs(link.expires_at) : null,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个短链吗？',
      onOk: async () => {
        await api.delete(`links/${id}`);
        message.success('删除成功');
        fetchLinks();
      },
    });
  };

  const handleSubmit = async (values: CreateLinkRequest & { expires_at?: dayjs.Dayjs }) => {
    try {
      const payload = {
        target_url: values.target_url,
        short_code: values.short_code,
        expires_at: values.expires_at ? values.expires_at.toISOString() : undefined,
      };
      if (editingLink) {
        await api.put(`links/${editingLink.id}`, { json: payload });
        message.success('更新成功');
      } else {
        await api.post('links', { json: payload });
        message.success('创建成功');
      }
      setModalOpen(false);
      fetchLinks();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns = [
    {
      title: '短链代码',
      dataIndex: 'short_code',
      key: 'short_code',
      render: (code: string) => {
        const shortUrl = `${window.location.origin}/${code}`;
        return (
          <a href={shortUrl} target="_blank" rel="noopener noreferrer">
            {code}
          </a>
        );
      },
    },
    { title: '目标 URL', dataIndex: 'target_url', key: 'target_url', ellipsis: true },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>{active ? '活跃' : '停用'}</Tag>
      ),
    },
    { title: '访问次数', dataIndex: 'visit_count', key: 'visit_count' },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: Link) => (
        <Space>
          <Button
            type="link"
            icon={<BarChartOutlined />}
            onClick={() => navigate(`/s/links/${record.id}`)}
          >
            详情
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>短链管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          创建短链
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={links}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          total,
          pageSize: 10,
          onChange: setPage,
        }}
      />
      <Modal
        title={editingLink ? '编辑短链' : '创建短链'}
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={() => setModalOpen(false)}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {editingLink && (
            <Form.Item label="短链代码">
              <Input value={editingLink.short_code} disabled />
              <span style={{ color: '#999', fontSize: 12 }}>短链代码创建后不可修改</span>
            </Form.Item>
          )}
          <Form.Item
            name="target_url"
            label="目标 URL"
            rules={[{ required: true, message: '请输入目标 URL' }]}
          >
            <Input placeholder="https://example.com/very/long/url" />
          </Form.Item>
          {!editingLink && (
            <Form.Item name="short_code" label="自定义短链（可选）">
              <Input placeholder="留空则自动生成" />
            </Form.Item>
          )}
          <Form.Item name="expires_at" label="过期时间（可选）">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
